import {PageStateManager, CoreEvent, BoxType, AssetType} from '@taopix/taopix-core';
import {StateManagerModuleInterface} from '@taopix/taopix-core/dist/types/Persistence/Modules';
import {PageInterface, PageItem, ReducerState} from '@taopix/taopix-core/dist/types/types';

export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export const getEntries = <T extends object>(obj: T): Entries<T> =>
  Object.entries(obj) as Entries<T>;

const emptyPictureBox = (item: PageItem): PageItem => {
  const res = {...item};

  res.hireswidth = 0;
  res.hiresheight = 0;
  res.rotatedhireswidth = 0;
  res.rotatedhiresheight = 0;
  res.hirespath = '';
  res.hiresfilelocation = 0;
  res.hiresfiletype = 106;

  /** If the image that is used contains an alpha channel we need to make sure we remove the whole mask
   * as the mask is drawn from the alpha channel. so if it was cleared the alpha channel of
   * the image would remain.
   */
  if (res.maskfiletype === 1) {
    res.maskref = '';
    res.maskfiletype = 0;
    res.maskhireswidth = 0;
    res.maskhiresheight = 0;
    res.maskscalingmode = 1;
    res.maskinvert = 0;
    res.maskfliphorizontal = 0;
    res.maskflipvertical = 0;
    res.maskfilelocation = 0;
  }

  // Reset picture set properties.
  res.rotation = 0;
  res.imagehorizonangle = 0;
  res.magnification = 100;
  res.imagebrightness = 0;
  res.imagecontrast = 0;
  res.imagecolourmode = 0;
  res.imageperfectlyclearenhance = 0;
  res.imageperfectlyclearredeye = 0;
  res.alpha = 100;
  res.fliphorizontal = 0;
  res.flipvertical = 0;
  res.dirty = true;
  res.zoomlock = 1;

  return res;
};

export default class ClearImages implements StateManagerModuleInterface {
  public call = async (manager: PageStateManager): Promise<ReducerState> => {
    return new Promise((resolve, reject) => {
      const entries = getEntries(manager.getPages());
      let len = entries.length;
      for (const [, page] of entries) {
        const newPage = {...page};
        for (const [ref, item] of getEntries(newPage.items)) {
          if (BoxType.Image === item.type && AssetType.User === item.hiresfiletype) {
            newPage.items[ref] = {
              ...emptyPictureBox(item),
            };
          }
        }

        this.dispatch(manager, newPage);

        if (!--len) {
          // update the asset usage
          manager.setLayout({
            ...manager.getLayout(),
            usagepicture: manager.getProjectAssetUsage(),
          });

          resolve(manager.getStoreState());
        }
      }
    });
  };

  private dispatch = (manager: PageStateManager, page: PageInterface) => {
    manager.updatePages([page]);

    PubSub.publish(`${CoreEvent.RELOAD_PAGE}.${page.pagenumber}`);
  };
}
